import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Chip, 
  Box, 
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  useTheme,
  Tooltip,
  alpha
} from '@mui/material';
import { 
  Person, 
  CalendarToday, 
  AccessTime,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  LocalHospital as LocalHospitalIcon,
  MedicalInformation as MedicalInformationIcon,
  Warning as WarningIcon,
  MedicationOutlined
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useAuth } from '../../auth/AuthContext';
import { claimPrescription } from '../../services/healinApi';

const PrescriptionCard = ({ prescription, onSelect, onClaim, onIssue }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [expandedMedications, setExpandedMedications] = useState(false);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedPatientInfo, setExpandedPatientInfo] = useState(false);

  const handleExpandMedications = () => {
    setExpandedMedications(!expandedMedications);
  };

  const handleOpenClaimDialog = () => {
    setClaimDialogOpen(true);
  };

  const handleCloseClaimDialog = () => {
    setClaimDialogOpen(false);
  };

  const handleOpenIssueDialog = () => {
    setIssueDialogOpen(true);
  };

  const handleCloseIssueDialog = () => {
    setIssueDialogOpen(false);
  };

  const handleClaim = async () => {
    if (!user || !user.username) {
      console.error('No authenticated user found');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const claimData = {
        userId: prescription.userId,
        requestId: prescription.requestId,
        assignedDoctorId: user.username
      };
      
      console.log('Claiming prescription with data:', claimData);
      const result = await claimPrescription(claimData);
      
      console.log('Prescription claimed successfully:', result);
        setClaimDialogOpen(false);
      
      // Call the onClaim callback if provided (for parent component to refresh data)
      if (onClaim) {
        onClaim(prescription.requestId);
      }
    } catch (error) {
      console.error('Error claiming prescription:', error);
      // You might want to show a user-friendly error message here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIssue = () => {
    if (onIssue) {
      onIssue();
    }
    setIssueDialogOpen(false);
  };

  const togglePatientInfo = () => {
    setExpandedPatientInfo(!expandedPatientInfo);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Safely access nested properties
  const getPatientInfo = () => {
    return prescription.patientInfo || {
      age: 'N/A',
      gender: 'N/A',
      bloodType: 'N/A',
      allergies: [],
      chronicConditions: []
    };
  };

  // Safely access medications
  const getMedications = () => {
    if (!prescription.medications || !Array.isArray(prescription.medications) || prescription.medications.length === 0) {
      return [];
    }
    return prescription.medications;
  };

  // Check if status is available (pending) for claiming
  const isAvailableStatus = (status) => {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'available' || lowerStatus === 'pending';
  };

  // Check if status is claimed for issuing
  const isClaimedStatus = (status) => {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'claimed';
  };

  // Check if status is issued
  const isIssuedStatus = (status) => {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'issued';
  };

  // Get status display info
  const getStatusInfo = () => {
    if (isIssuedStatus(prescription.status)) {
      return { label: "Issued", color: "secondary" };
    } else if (isClaimedStatus(prescription.status)) {
      return { label: "Claimed", color: "primary" };
    } else {
      return { label: "Available", color: "success" };
    }
  };

  return (
    <>
      <Card 
        elevation={3} 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          },
          cursor: onSelect ? 'pointer' : 'default'
        }}
        onClick={onSelect ? () => onSelect() : undefined}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h6" component="div" gutterBottom noWrap>
              Prescription Request
            </Typography>
            <Chip 
              label={getStatusInfo().label} 
              color={getStatusInfo().color} 
              size="small" 
              sx={{ ml: 1 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Person fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Patient: {prescription.patientName}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarToday fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Requested: {formatDate(prescription.requestedAt)}
            </Typography>
          </Box>
          
          {isIssuedStatus(prescription.status) && prescription.issueDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTime fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Issued: {formatDate(prescription.issueDate)}
              </Typography>
            </Box>
          )}
          
          {prescription.status === 'CLAIMED' && prescription.doctorId && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTime fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Doctor ID: {prescription.doctorId}
              </Typography>
            </Box>
          )}

          {/* Patient Information Section */}
          <Box 
            sx={{ 
              mt: 2, 
              p: 1, 
              borderRadius: 1, 
              bgcolor: alpha(theme.palette.info.main, 0.08)
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MedicalInformationIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main }} />
                <Typography variant="subtitle2" color="text.primary">
                  Patient Information
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  togglePatientInfo(); 
                }}
                sx={{ p: 0, cursor: 'pointer' }}
              >
                {expandedPatientInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
          
          <Collapse in={expandedPatientInfo} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1, ml: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <InfoIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                <Typography variant="body2">
                Age: {getPatientInfo().age}, Gender: {getPatientInfo().gender}
              </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <LocalHospitalIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                <Typography variant="body2">
                Blood Type: {getPatientInfo().bloodType}
              </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>Chronic Conditions:</Typography>
              <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2 }}>
                {getPatientInfo().chronicConditions.map((condition, index) => (
                  <Typography component="li" variant="body2" key={index}>
                    {condition}
                  </Typography>
                ))}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
                <WarningIcon fontSize="small" sx={{ mr: 1, color: theme.palette.warning.main, fontSize: '0.9rem', mt: 0.3 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Allergies:</Typography>
                  {getPatientInfo().allergies.length > 0 ? (
                    <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                      {getPatientInfo().allergies.map((allergy, index) => (
                        <Typography component="li" variant="body2" key={index} color="warning.main">
                          {allergy}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2">No known allergies</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Collapse>

          {/* Medications Section */}
          <Box 
            sx={{ 
              mt: 2, 
              p: 1, 
              borderRadius: 1, 
              bgcolor: alpha(theme.palette.primary.main, 0.08)
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MedicationOutlined fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="subtitle2" color="text.primary">
                  Medications ({getMedications().length})
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  handleExpandMedications(); 
                }}
                sx={{ cursor: 'pointer' }}
              >
                {expandedMedications ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
          
          <Collapse in={expandedMedications} timeout="auto" unmountOnExit>
            {getMedications().length > 0 ? (
            <List dense disablePadding>
              {getMedications().map((medication, index) => (
                  <React.Fragment key={medication.id || index}>
                  <ListItem 
                    sx={{ 
                      py: 1, 
                      px: 1,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <MedicationOutlined fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {medication.name || 'Unnamed Medication'}
                          </Typography>
                            {medication.condition && (
                          <Tooltip title={`For ${medication.condition}`}>
                            <Chip 
                              label={medication.condition} 
                              size="small" 
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main
                              }} 
                            />
                          </Tooltip>
                            )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                            {medication.dosage ? `${medication.dosage}, ` : ''}
                            {medication.frequency || 'No frequency specified'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < getMedications().length - 1 && (
                    <Divider component="li" variant="inset" sx={{ opacity: 0.6 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No medications found in this prescription request.
                </Typography>
              </Box>
            )}
          </Collapse>

          {prescription.comments && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Notes: {prescription.comments}
              </Typography>
            </Box>
          )}
        </CardContent>
        
        <CardActions sx={{ p: 2, pt: 0 }}>
          {isAvailableStatus(prescription.status) && (
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth
              onClick={(e) => { e.stopPropagation(); handleOpenClaimDialog(); }}
            >
              Claim Prescription
            </Button>
          )}
          
          {isClaimedStatus(prescription.status) && onIssue && (
            <Button 
              variant="contained" 
              color="primary" 
              fullWidth
              onClick={(e) => { e.stopPropagation(); handleOpenIssueDialog(); }}
            >
              Issue Prescription
            </Button>
          )}
          
          {isIssuedStatus(prescription.status) && (
            <Button 
              variant="outlined" 
              color="secondary" 
              fullWidth
              disabled
              sx={{ 
                cursor: 'default',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              Prescription Issued
            </Button>
          )}
        </CardActions>
      </Card>
      
      {/* Claim Dialog */}
      <Dialog
        open={claimDialogOpen}
        onClose={handleCloseClaimDialog}
        aria-labelledby="claim-dialog-title"
        aria-describedby="claim-dialog-description"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle id="claim-dialog-title">
          Confirm Claim
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="claim-dialog-description">
            Are you sure you want to claim this prescription request for {prescription.patientName}? 
            You will be responsible for reviewing and issuing {getMedications().length} medication(s).
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Medications:</Typography>
            <List dense>
              {getMedications().map((med) => (
                <ListItem key={med.id} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={med.name}
                    secondary={`${med.dosage}, ${med.frequency}`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClaimDialog} color="inherit" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleClaim} color="primary" variant="contained" autoFocus disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Confirm Claim'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Issue Dialog */}
      <Dialog
        open={issueDialogOpen}
        onClose={handleCloseIssueDialog}
        aria-labelledby="issue-dialog-title"
        aria-describedby="issue-dialog-description"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle id="issue-dialog-title">
          Confirm Issue
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="issue-dialog-description">
            Are you sure you want to issue this prescription for {prescription.patientName}?
            This will open the detailed prescription view where you can review and finalize the prescription.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIssueDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleIssue} color="primary" variant="contained" autoFocus>
            Review & Issue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

PrescriptionCard.propTypes = {
  prescription: PropTypes.shape({
    requestId: PropTypes.string.isRequired,
    patientName: PropTypes.string,
    userId: PropTypes.string.isRequired,
    patientInfo: PropTypes.shape({
      age: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      gender: PropTypes.string,
      bloodType: PropTypes.string,
      allergies: PropTypes.arrayOf(PropTypes.string),
      chronicConditions: PropTypes.arrayOf(PropTypes.string)
    }),
    medications: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string.isRequired,
        dosage: PropTypes.string,
        frequency: PropTypes.string,
        condition: PropTypes.string
      })
    ),
    requestedAt: PropTypes.string,
    status: PropTypes.string.isRequired,
    comments: PropTypes.string,
    doctorId: PropTypes.string
  }).isRequired,
  onSelect: PropTypes.func,
  onClaim: PropTypes.func,
  onIssue: PropTypes.func
};

PrescriptionCard.defaultProps = {
  onSelect: null,
  onClaim: null,
  onIssue: null
};

export default PrescriptionCard; 